import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.setAttribute("data-portal-modal", "true");
    div.setAttribute("translate", "no");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

export default function Capacitaciones({ usuario, onBackToMenu }) {
  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed; 
    } catch (e) { return {}; }
  }, [usuario]);

  const cargoUpper = String(usuario?.cargo || "").toUpperCase().trim();
  const esAdminGlobal = cargoUpper === "SUPER_ADMIN";

  const tienePermisoPlataforma = esAdminGlobal || permisosABAC['Plataforma de Capacitaciones'] !== undefined;
  const tienePermisoReportes = esAdminGlobal || permisosABAC['Ver Reportes'] !== undefined;
  const tienePermisoPublicar = esAdminGlobal || permisosABAC['Publicar Actividad'] !== undefined;

  const [cursos, setCursos] = useState([]);
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorCarga, setErrorCarga] = useState(false);
  
  const [completadosLocal, setCompletadosLocal] = useState([]);
  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });

  const alertTitle = customAlert.type === 'success' ? '¡Éxito!' : customAlert.type === 'warning' ? 'Atención' : 'Error';
  const alertIcon = customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌';
  const alertButtonClass = customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/30' : customAlert.type === 'warning' ? 'bg-amber-500 hover:bg-amber-400 shadow-amber-500/30' : 'bg-red-600 hover:bg-red-500 shadow-red-600/30';

  const [modalVideo, setModalVideo] = useState(null);
  const [videoTerminado, setVideoTerminado] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [modalReportes, setModalReportes] = useState(false);
  const [modalCrear, setModalCrear] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [nuevoCurso, setNuevoCurso] = useState({
    titulo: "", categoria: "SEGURIDAD Y SALUD (SST)", tipo: "video", url_recurso: "", duracion: "10 min", descripcion: ""
  });

  const cargarCursos = async () => {
    setLoading(true); setErrorCarga(false);
    try {
      const url = new URL(`${process.env.REACT_APP_API_URL}/api/capacitaciones`);
      if (usuario && usuario.documento) url.searchParams.append("documento", usuario.documento);
      const token = localStorage.getItem("token");
      const res = await fetch(url, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          "x-user-data": JSON.stringify(usuario), "x-abac-permissions": JSON.stringify(permisosABAC)
        }
      });
      const data = await res.json();
      if (data.success) setCursos(data.cursos || []); else setErrorCarga(true);
    } catch (err) { setErrorCarga(true); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (tienePermisoPlataforma || tienePermisoPublicar) cargarCursos(); else setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarReportes = async () => {
    setModalReportes(true);
    try {
      const url = new URL(`${process.env.REACT_APP_API_URL}/api/capacitaciones/reportes`);
      const token = localStorage.getItem("token");
      const res = await fetch(url, {
        headers: { ...(token && { Authorization: `Bearer ${token}` }), "x-user-data": JSON.stringify(usuario), "x-abac-permissions": JSON.stringify(permisosABAC) }
      });
      const data = await res.json();
      if (data.success) setReportes(data.reportes);
    } catch (error) { console.error(error); }
  };

  const handleCrearCurso = async (e) => {
    e.preventDefault();
    if (!tienePermisoPublicar || isSubmitting) return;
    if (!nuevoCurso.titulo) return setCustomAlert({ show: true, message: "Ingresa el título.", type: "warning" });
    if (!nuevoCurso.url_recurso) return setCustomAlert({ show: true, message: "Ingresa un enlace válido.", type: "warning" });

    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/capacitaciones`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(nuevoCurso)
      });
      const data = await res.json();
      if (data.success) {
        setCustomAlert({ show: true, message: "¡Publicado exitosamente!", type: "success" });
        setNuevoCurso({ titulo: "", categoria: "SEGURIDAD Y SALUD (SST)", tipo: "video", url_recurso: "", duracion: "10 min", descripcion: "" });
        setModalCrear(false); cargarCursos();
      } else {
        setCustomAlert({ show: true, message: data.message || "Error al publicar.", type: "error" });
      }
    } catch (err) { setCustomAlert({ show: true, message: "Error de conexión.", type: "error" }); } finally { setIsSubmitting(false); }
  };

  const handleEliminarCapacitacion = async (id) => {
    if (!window.confirm("¿Eliminar esta capacitación? Esta acción no se puede deshacer.")) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/capacitaciones/${id}`, {
        method: "DELETE", headers: { ...(token && { Authorization: `Bearer ${token}` }), "x-user-data": JSON.stringify(usuario), "x-abac-permissions": JSON.stringify(permisosABAC) }
      });
      const data = await res.json();
      if (data.success) {
        setCustomAlert({ show: true, message: "¡Eliminada correctamente!", type: "success" });
        setCursos(prev => prev.filter(c => c.id !== id));
      } else { setCustomAlert({ show: true, message: data.message || "Error al eliminar.", type: "error" }); }
    } catch (err) { setCustomAlert({ show: true, message: "Error de conexión.", type: "error" }); }
  };

  const handleCompletarCurso = async () => {
    if (!videoTerminado && modalVideo?.tipo === "video") return setCustomAlert({ show: true, message: "Debes terminar de ver el video.", type: "warning" });
    setGuardando(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/capacitaciones/completar`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ documento: usuario.documento, curso_id: modalVideo.id })
      });
      const data = await res.json();
      if (data.success) {
        setCustomAlert({ show: true, message: "¡Has completado esta capacitación!", type: "success" });
        setCompletadosLocal(prev => [...prev, modalVideo.id]); setModalVideo(null);
      } else { setCustomAlert({ show: true, message: data.message || "Error al guardar.", type: "error" }); }
    } catch (err) { setCustomAlert({ show: true, message: "Error al intentar guardar.", type: "error" }); } finally { setGuardando(false); }
  };

  const obtenerEmbedYoutube = (url) => {
    if (!url) return "";
    const regExp = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i;
    const match = url.match(regExp);
    return match && match[1].length === 11 ? `https://www.youtube.com/embed/${match[1]}?enablejsapi=1` : url;
  };
  const isVideoDirecto = (url) => {
    if (!url) return false;
    const API_URL = process.env.REACT_APP_API_URL || "";
    return url.startsWith("/") || url.startsWith(API_URL) || url.startsWith("data:video") || url.endsWith(".mp4");
  };

  if (!tienePermisoPlataforma && !tienePermisoPublicar && !tienePermisoReportes) {
    return (
      <div className="bg-white/60 backdrop-blur-3xl rounded-[36px] shadow-2xl p-10 max-w-4xl w-full mx-auto border border-white/80 text-center animate-fade-in-up">
        <span className="text-6xl block mb-6">🛡️</span>
        <h2 className="text-3xl font-black text-slate-800 mb-2">Acceso Restringido</h2>
        <p className="text-sm text-slate-600 mb-8 font-bold">No cuentas con privilegios asignados en la matriz ABAC para visualizar este módulo.</p>
        <button onClick={onBackToMenu} className="btn-glow px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl transition cursor-pointer">Volver al Menú</button>
      </div>
    );
  }

  return (
    <div className="bg-white/40 backdrop-blur-[45px] rounded-[36px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-6 sm:p-10 max-w-7xl w-full mx-auto border border-white/80 max-h-[92vh] overflow-y-auto custom-scrollbar animate-fade-in relative">
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-200/50 pb-6 mb-8 gap-4 animate-fade-in-down">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-blue-50/80 border border-blue-100 flex items-center justify-center text-3xl shadow-inner text-blue-600 icon-bounce">🎓</div>
          <div>
            <h2 className="text-2xl font-black text-slate-800 tracking-tight">Plataforma de Capacitaciones</h2>
            <p className="text-sm text-slate-500 font-bold mt-1">{tienePermisoPublicar ? "Panel corporativo y publicación de contenido." : "Tus cursos y actividades interactivas."}</p>
          </div>
        </div>
        
        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          {tienePermisoReportes && (
            <button onClick={cargarReportes} className="px-5 py-2.5 rounded-2xl bg-white/70 hover:bg-white text-slate-700 text-xs font-bold transition-all border border-slate-200 shadow-sm cursor-pointer hover:-translate-y-0.5">📊 Ver Reportes</button>
          )}
          {tienePermisoPublicar && (
            <button onClick={() => setModalCrear(true)} className="btn-glow px-5 py-2.5 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-black shadow-lg shadow-blue-500/30 transition-all cursor-pointer hover:-translate-y-0.5">+ Publicar Enlace</button>
          )}
          <button onClick={onBackToMenu} className="px-5 py-2.5 rounded-2xl bg-white/70 hover:bg-white text-slate-700 text-xs font-bold transition-all border border-slate-200 shadow-sm cursor-pointer hover:-translate-y-0.5">Volver</button>
        </div>
      </div>

      {tienePermisoPlataforma && (
        <div>
          {loading ? (
            <div className="text-center py-16 animate-pulse-soft">
              <span className="text-5xl inline-block mb-3 icon-bounce">🎓</span>
              <p className="text-sm text-slate-500 font-black tracking-widest uppercase">Cargando...</p>
            </div>
          ) : errorCarga ? (
            <div className="bg-white/60 backdrop-blur-xl p-12 rounded-[32px] border border-white/80 shadow-sm text-center flex flex-col items-center justify-center min-h-[300px] animate-fade-in-up delay-100">
              <span className="text-5xl block mb-4">⚠️</span>
              <h3 className="text-base font-black text-slate-800 mb-2">No se pudo conectar</h3>
              <p className="text-xs text-slate-600 font-bold mb-6">Verifica el backend.</p>
              <button onClick={cargarCursos} className="btn-glow px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-xs font-black transition cursor-pointer">Reintentar</button>
            </div>
          ) : cursos.length === 0 ? (
            <div className="bg-white/60 backdrop-blur-xl p-12 rounded-[32px] border border-white/80 shadow-sm text-center flex flex-col items-center justify-center min-h-[300px] animate-fade-in-up delay-100">
              <span className="text-6xl block mb-4 icon-bounce">📚</span>
              <h3 className="text-lg font-black text-slate-800">No hay capacitaciones aún</h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cursos.map((curso, index) => {
                const estaCompletado = curso.completado || completadosLocal.includes(curso.id);
                return (
                  <div key={curso.id} className={`bg-white/70 backdrop-blur-xl rounded-[28px] border border-white shadow-sm hover:shadow-xl transition-all group flex flex-col relative card-hover glass-reflection animate-fade-in-up delay-${(index % 5 + 1) * 100}`}>
                    {tienePermisoPublicar && (
                      <button onClick={(e) => { e.stopPropagation(); handleEliminarCapacitacion(curso.id); }} className="absolute top-4 right-16 w-9 h-9 bg-rose-500/10 hover:bg-rose-500 text-rose-600 hover:text-white rounded-full flex items-center justify-center transition-all shadow-sm border border-rose-200/50 cursor-pointer z-20">🗑️</button>
                    )}
                    <div className={`h-40 relative flex items-center justify-center transition cursor-pointer rounded-t-[28px] overflow-hidden ${curso.tipo === "kahoot" ? "bg-purple-900/90" : "bg-slate-900/90"}`} onClick={() => { setModalVideo(curso); setVideoTerminado(curso.tipo === "kahoot"); }}>
                      {estaCompletado && (
                        <div className="absolute top-4 left-4 bg-emerald-500/90 backdrop-blur-md text-white text-[10px] px-3 py-1.5 rounded-xl font-bold flex items-center shadow-md border border-emerald-400 z-10 animate-fade-in">✓ Completada</div>
                      )}
                      <span className="text-6xl group-hover:scale-110 transition-transform duration-300 icon-bounce">{curso.tipo === "kahoot" ? "🎮" : "▶️"}</span>
                      <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md text-white text-[10px] px-3 py-1.5 rounded-xl font-bold z-10">{curso.duracion || "10 min"}</div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <div>
                        <span className="text-[10px] font-black text-blue-700 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-lg uppercase tracking-wider">{curso.categoria}</span>
                        <h3 className="font-black text-slate-800 mt-3 text-base leading-tight">{curso.titulo}</h3>
                        <p className="text-xs text-slate-500 font-bold mt-2 line-clamp-2">{curso.descripcion || "Sin descripción."}</p>
                      </div>
                      <button onClick={() => { setModalVideo(curso); setVideoTerminado(curso.tipo === "kahoot"); }} className={`mt-6 w-full py-3 text-xs font-black rounded-2xl transition border cursor-pointer shadow-sm hover:-translate-y-0.5 ${curso.tipo === "kahoot" ? (estaCompletado ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200") : (estaCompletado ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100")}`}>
                        {curso.tipo === "kahoot" ? (estaCompletado ? "Volver a Jugar" : "Iniciar Juego 🎮") : (estaCompletado ? "Volver a ver" : "Ver Video 📝")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* MODAL: VIDEO */}
      {modalVideo && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex justify-center items-center p-4 z-[9998] animate-fade-in">
            <div className="bg-white/90 backdrop-blur-3xl rounded-[36px] max-w-3xl w-full shadow-[0_30px_90px_rgba(0,0,0,0.3)] border border-white flex flex-col relative animate-modal-pop" style={{ maxHeight: '90vh' }}>
              <div className="bg-slate-900 px-6 py-5 flex items-center text-white rounded-t-[36px]">
                <h3 className="font-bold truncate text-sm pr-10">{modalVideo.titulo}</h3>
                <button onClick={() => setModalVideo(null)} className="absolute right-5 top-4 bg-white/10 hover:bg-white/20 font-bold w-7 h-7 rounded-full flex items-center justify-center transition cursor-pointer text-xs">✕</button>
              </div>
              <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                {modalVideo.tipo === "video" ? (
                  <div className="bg-black w-full aspect-video rounded-3xl overflow-hidden flex items-center justify-center shadow-inner">
                    {isVideoDirecto(modalVideo.url_recurso) ? (
                      <video src={modalVideo.url_recurso} controls onEnded={() => setVideoTerminado(true)} className="w-full h-full object-contain" />
                    ) : (
                      <iframe className="w-full h-full" src={obtenerEmbedYoutube(modalVideo.url_recurso)} title={modalVideo.titulo} allowFullScreen onLoad={() => setVideoTerminado(true)}></iframe>
                    )}
                  </div>
                ) : (
                  <div className="bg-purple-900/90 text-white p-12 rounded-[32px] shadow-xl text-center">
                    <span className="text-7xl block mb-6 icon-bounce">🎮</span>
                    <h2 className="text-2xl font-black mb-6">{modalVideo.titulo}</h2>
                    <a href={modalVideo.url_recurso} target="_blank" rel="noreferrer" className="btn-glow inline-block px-10 py-4 bg-yellow-400 hover:bg-yellow-300 text-purple-950 font-black text-sm rounded-2xl shadow-lg transition">Abrir Juego</a>
                  </div>
                )}
                <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50 p-5 rounded-[24px] border border-slate-200">
                  <p className="text-xs text-slate-700 font-bold text-center sm:text-left">{videoTerminado || modalVideo.tipo !== "video" ? "✅ Ya puedes registrar tu asistencia." : "⚠️ Termina de ver para continuar."}</p>
                  <button onClick={handleCompletarCurso} disabled={!videoTerminado || guardando} className={`btn-glow px-8 py-3.5 rounded-2xl text-xs font-black transition w-full sm:w-auto ${videoTerminado || modalVideo.tipo !== "video" ? "bg-emerald-600 text-white cursor-pointer" : "bg-slate-300 text-slate-500 cursor-not-allowed"}`}>
                    {guardando ? "Guardando..." : "Marcar Completado"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </SafePortalModal>
      )}

      {/* MODAL: REPORTES Y CREAR (Se les aplica .animate-modal-pop a sus contenedores de la misma forma) */}
      {modalReportes && tienePermisoReportes && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex justify-center items-center p-4 z-[9998] animate-fade-in">
            <div className="bg-white/90 backdrop-blur-3xl rounded-[36px] max-w-5xl w-full p-8 shadow-[0_30px_90px_rgba(0,0,0,0.3)] border border-white flex flex-col animate-modal-pop" style={{ maxHeight: '90vh' }}>
              <div className="flex justify-between items-center border-b border-slate-200 pb-5 mb-6">
                <div>
                  <h3 className="text-2xl font-black text-slate-800">Reporte de Capacitaciones</h3>
                  <p className="text-xs text-slate-500 font-bold mt-1">Historial de formaciones completadas.</p>
                </div>
                <button onClick={() => setModalReportes(false)} className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold w-8 h-8 rounded-full flex items-center justify-center transition cursor-pointer">✕</button>
              </div>
              <div className="overflow-y-auto flex-1 border border-slate-200 rounded-[24px] bg-white shadow-inner custom-scrollbar">
                <table className="w-full text-left text-xs text-slate-700">
                  <thead className="bg-slate-50 sticky top-0 border-b border-slate-200 font-black">
                    <tr>
                      <th className="py-4 px-5">Fecha</th><th className="py-4 px-5">Colaborador</th><th className="py-4 px-5">Sede</th><th className="py-4 px-5">Actividad</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium">
                    {reportes.length > 0 ? reportes.map((rep, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 transition">
                        <td className="py-3.5 px-5">{new Date(rep.fecha_completado).toLocaleString()}</td>
                        <td className="py-3.5 px-5 font-bold">{rep.nombres} {rep.apellidos}</td>
                        <td className="py-3.5 px-5">{rep.centro_trabajo}</td>
                        <td className="py-3.5 px-5 text-blue-600 font-bold">{rep.titulo}</td>
                      </tr>
                    )) : <tr><td colSpan={4} className="text-center py-12 text-slate-500">No hay registros aún.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </SafePortalModal>
      )}

      {modalCrear && tienePermisoPublicar && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex justify-center items-center p-4 z-[9998] animate-fade-in">
            <div className="bg-white/90 backdrop-blur-3xl rounded-[36px] max-w-xl w-full border border-white flex flex-col animate-modal-pop">
              <div className="px-8 py-6 border-b border-slate-200 bg-white/60 flex justify-between items-center rounded-t-[36px]">
                <h3 className="text-xl font-black text-slate-800">🚀 Publicar Formación</h3>
                <button onClick={() => setModalCrear(false)} className="bg-slate-100 w-8 h-8 rounded-full flex justify-center items-center font-bold text-slate-600">✕</button>
              </div>
              <div className="p-8">
                <form id="form-crear" onSubmit={handleCrearCurso} className="space-y-5">
                  <div>
                    <label className="block text-xs font-black text-slate-700 mb-2">Título *</label>
                    <input required type="text" value={nuevoCurso.titulo} onChange={e => setNuevoCurso({...nuevoCurso, titulo: e.target.value})} className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold text-slate-800" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-black text-slate-700 mb-2">Tipo *</label>
                      <select value={nuevoCurso.tipo} onChange={e => setNuevoCurso({...nuevoCurso, tipo: e.target.value})} className="w-full h-[52px] px-5 rounded-2xl border border-slate-200 bg-slate-50 text-sm font-bold">
                        <option value="video">📹 Video</option><option value="kahoot">🎮 Juego</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-black text-slate-700 mb-2">Categoría *</label>
                      <select value={nuevoCurso.categoria} onChange={e => setNuevoCurso({...nuevoCurso, categoria: e.target.value})} className="w-full h-[52px] px-5 rounded-2xl border border-slate-200 bg-slate-50 text-sm font-bold">
                        <option value="SEGURIDAD Y SALUD (SST)">SST</option><option value="GESTIÓN HUMANA">Gestión Humana</option><option value="OPERATIVO">Operativo</option>
                      </select>
                    </div>
                  </div>
                  <div className="bg-blue-50/50 border border-blue-100 rounded-[24px] p-6 shadow-inner">
                    <label className="block text-xs font-black text-blue-900 mb-2">Enlace del Recurso (URL) *</label>
                    <input required type="url" value={nuevoCurso.url_recurso} onChange={e => setNuevoCurso({...nuevoCurso, url_recurso: e.target.value})} className="w-full px-5 py-4 rounded-2xl border border-blue-200 bg-white font-mono text-sm text-blue-700 font-bold focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="https://..." />
                  </div>
                </form>
              </div>
              <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 rounded-b-[36px] flex justify-end gap-3">
                <button type="button" onClick={() => setModalCrear(false)} className="px-6 py-3.5 bg-white text-slate-700 rounded-2xl text-xs font-bold border border-slate-200">Cancelar</button>
                <button type="submit" form="form-crear" disabled={isSubmitting} className="btn-glow px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl text-xs font-black">Publicar Ahora</button>
              </div>
            </div>
          </div>
        </SafePortalModal>
      )}

      {/* MODAL ALERTA */}
      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/90 backdrop-blur-3xl rounded-[36px] p-8 max-w-sm w-full text-center shadow-2xl animate-modal-pop">
              <div className={`w-24 h-24 mx-auto rounded-[28px] flex items-center justify-center mb-6 shadow-sm ${customAlert.type === 'success' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                <span className="text-5xl icon-bounce">{alertIcon}</span>
              </div>
              <h3 className="text-2xl font-black text-slate-800 mb-3">{alertTitle}</h3>
              <p className="text-sm text-slate-600 font-bold mb-8">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-4 rounded-2xl font-black text-white ${alertButtonClass}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}